import { memo, ReactNode, useMemo, useState } from "react";
import {
  Grid,
  Box,
  Card,
  CardActionArea,
  Typography,
  Avatar,
  Button,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { Modal, useFormContext } from "@4uhub/lib4uhub";
import { TIServiceForm } from "../iServiceSchema";
import { ClearConfirmationModal } from "./ClearConfirmationModal";
import { useWatch } from "react-hook-form";

interface IIServiceTypeProps {
  img?: {
    src: string;
    alt: string;
    sx?: Record<string, any>;
    variant?: "circular" | "rounded" | "square";
  };
  icon?: ReactNode;
  serviceName: string;
  iServiceTypeName: keyof TIServiceForm;
  isSelected: boolean;
  isEditingScreen: boolean;
  getFormattedData?: (values: TIServiceForm) => Record<string, any> | undefined;
  onConfirm: () => void;
  onClearConfigs: () => void;
  onOpen: () => void;
  onCloseSelected: () => void;
  renderComponent: (props: { namePrefix: keyof TIServiceForm }) => JSX.Element;
  showConfirmButton?: boolean;
}

const translationPath = "page.4uBus.register.iService.";

export const IServiceType = memo(
  ({
    img,
    icon,
    serviceName,
    iServiceTypeName,
    isSelected,
    isEditingScreen,
    getFormattedData,
    onConfirm,
    onClearConfigs,
    onOpen,
    onCloseSelected,
    renderComponent,
    showConfirmButton = true,
  }: IIServiceTypeProps) => {
    const { t } = useTranslation();

    const [open, setOpen] = useState(false);

    const [openDeleteAlert, setOpenDeleteAlert] = useState(false);

    const { trigger, setValue, getValues, control } =
      useFormContext<TIServiceForm>();

    const configs = useWatch({
      control,
      name: "configs",
    });

    const uBusUser = useWatch({
      control,
      name: "uBusUser",
    });

    const isCardBlocked = useMemo(
      () => !!((configs || uBusUser) && !isSelected),
      [configs, uBusUser, isSelected],
    );

    const handleOpen = () => {
      setOpen(true);
      if (!isSelected) {
        onOpen();
      }
    };

    const handleClose = () => setOpen(false);

    const handleCloseSelected = () => {
      onCloseSelected();
      setOpen(false);
    };

    const handleConfirm = async () => {
      const isValid = await trigger(iServiceTypeName);
      if (!isValid) return;
      if (getFormattedData) {
        const currentValues = getValues();
        const dataToSerialize = getFormattedData(currentValues);
        setValue("configs", JSON.stringify(dataToSerialize));
      }
      onConfirm();
      handleClose();
    };

    const handleCancel = () => {
      setValue(iServiceTypeName, null);
      handleClose();
    };

    const handleOpenDeleteAlert = (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpenDeleteAlert(true);
    };

    const handleCloseAlert = () => setOpenDeleteAlert(false);

    const handleClearConfigs = () => {
      setValue("configs", null);
      setValue(iServiceTypeName, null);
      onClearConfigs && onClearConfigs();
      handleCloseAlert();
      if (open) {
        handleClose();
      }
    };

    return (
      <Grid item xs={12} sm={6}>
        <Card
          variant="outlined"
          sx={{
            position: "relative",
            borderColor: isSelected ? "primary.main" : "divider",
            bgcolor: isSelected ? "action.hover" : "inherit",
            opacity: isCardBlocked ? 0.6 : 1,
            filter: isCardBlocked ? "grayscale(0.5)" : "none",
          }}
        >
          {isSelected && !isEditingScreen && (
            <Tooltip title={t(translationPath + "clear_options")}>
              <IconButton
                size="small"
                color="error"
                onClick={handleOpenDeleteAlert}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  zIndex: 1,
                  backgroundColor: "background.paper",
                  "&:hover": { backgroundColor: "#fee2e2" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <CardActionArea
            onClick={isCardBlocked ? undefined : handleOpen}
            disableRipple={isCardBlocked}
            disableTouchRipple={isCardBlocked}
            sx={{
              cursor: isCardBlocked ? "not-allowed" : "pointer",
              ...(isCardBlocked && {
                "& .MuiCardActionArea-focusHighlight": {
                  display: "none",
                },
              }),
            }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                minHeight: 64,
                pr: isSelected ? 6 : 2,
              }}
            >
              {(img || icon) && (
                <Avatar
                  src={img?.src}
                  alt={img?.alt}
                  variant={img?.variant}
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    "& img": {
                      objectFit: "contain",
                    },
                    ...img?.sx,
                  }}
                >
                  {!img && icon}
                </Avatar>
              )}
              <Box sx={{ overflow: "hidden" }}>
                <Typography fontWeight="medium" noWrap>
                  {serviceName}
                </Typography>
                {isSelected && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ display: "block" }}
                  >
                    {t(translationPath + "selected_service")}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardActionArea>
        </Card>

        <Modal
          open={open}
          onClose={isSelected ? handleCloseSelected : handleCancel}
          title={
            isEditingScreen
              ? t(translationPath + "selected_options") || "Selected Options"
              : t(translationPath + "select_options") || "Select the Options"
          }
        >
          <Box component="form" sx={{ p: 1 }}>
            <Grid container spacing={2} sx={{ paddingY: 4 }}>
              {renderComponent({ namePrefix: iServiceTypeName })}
            </Grid>

            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 4, pb: 1 }}
            >
              <Stack direction="row" spacing={2}>
                {showConfirmButton && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirm}
                  >
                    {t(translationPath + "define")}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={isSelected ? handleCloseSelected : handleCancel}
                >
                  {t(translationPath + "cancel")}
                </Button>
              </Stack>

              {isSelected && !isEditingScreen && (
                <Button color="error" onClick={(e) => handleOpenDeleteAlert(e)}>
                  {t(translationPath + "clear_options") || "Clear Options"}
                </Button>
              )}
            </Stack>
          </Box>
        </Modal>

        <ClearConfirmationModal
          openDeleteAlert={openDeleteAlert}
          handleConfirmClear={handleClearConfigs}
          handleCloseAlert={handleCloseAlert}
        />
      </Grid>
    );
  },
);
